import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Plus, Edit, ChevronLeft, Search, Trash2 } from 'lucide-react';
import { RecordActions } from '../../../components/common/RecordActions';
import { RecordViewDialog } from '../../../components/common/RecordViewDialog';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';

const StatusProfiles = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('statusprofiles');
  const [viewItem, setViewItem] = useState(null);
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [formData, setFormData] = useState({
    profile_id: '', name: '', description: '', object_type: 'PROJECT',
    user_statuses: [], allow_multiple_active: false, status: 'Active'
  });

  const [newStatus, setNewStatus] = useState({
    status_number: 1, status_code: '', status_name: '', description: '',
    sequence: 1, is_initial: false, is_final: false, allowed_transitions: [],
    block_actual_postings: false, block_planning: false, block_budget: false, block_release: false
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await psAPI.getStatusProfiles();
      setProfiles(response.data);
    } catch (error) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleCreate = () => {
    setEditMode(false);
    setFormData({ profile_id: '', name: '', description: '', object_type: 'PROJECT', user_statuses: [], allow_multiple_active: false, status: 'Active' });
    setShowDialog(true);
  };

  const handleEdit = (profile) => {
    setEditMode(true);
    setFormData({ ...profile, description: profile.description || '', user_statuses: profile.user_statuses || [] });
    setShowDialog(true);
  };

  const addStatus = () => {
    if (!newStatus.status_code || !newStatus.status_name) { toast.error('Code and name required'); return; }
    const existingNumbers = formData.user_statuses.map(s => s.status_number);
    if (existingNumbers.includes(newStatus.status_number)) { toast.error('Number exists'); return; }
    setFormData({ ...formData, user_statuses: [...formData.user_statuses, {...newStatus}] });
    setNewStatus({ ...newStatus, status_number: Math.max(...existingNumbers, 0) + 1, status_code: '', status_name: '', sequence: formData.user_statuses.length + 2 });
  };

  const removeStatus = (index) => {
    setFormData({ ...formData, user_statuses: formData.user_statuses.filter((_, i) => i !== index) });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.profile_id || !formData.name) { toast.error('Fill required fields'); return; }
      if (editMode) { await psAPI.updateStatusProfile(formData.profile_id, formData); toast.success('Updated'); }
      else { await psAPI.createStatusProfile(formData); toast.success('Created'); }
      setShowDialog(false); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const filteredProfiles = profiles.filter(p => p.profile_id.toLowerCase().includes(searchTerm.toLowerCase()) || p.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <Layout>
    <div className="p-6 space-y-6" data-testid="status-profiles-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/ps')}><ChevronLeft className="h-5 w-5" /></Button>
        <div className="flex-1"><h1 className="text-2xl font-bold">Status Profiles</h1><p className="text-gray-500">Define user statuses and transition rules</p></div>
        {writable && (<Button onClick={handleCreate}><Plus className="h-4 w-4 mr-2" />New Profile</Button>)}
      </div>
      <Card><CardContent className="pt-6"><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" /><Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div></CardContent></Card>
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Profile ID</TableHead><TableHead>Name</TableHead><TableHead>Object Type</TableHead><TableHead>Statuses</TableHead><TableHead>Status</TableHead><TableHead className="w-[80px]">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={6} className="text-center py-8">Loading...</TableCell></TableRow> :
            filteredProfiles.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">No profiles</TableCell></TableRow> :
            filteredProfiles.map((p) => (
              <TableRow key={p.profile_id}>
                <TableCell className="font-medium">{p.profile_id}</TableCell>
                <TableCell>{p.name}</TableCell>
                <TableCell><Badge variant="outline">{p.object_type}</Badge></TableCell>
                <TableCell>{p.status_count || 0}</TableCell>
                <TableCell><Badge variant={p.status === 'Active' ? 'default' : 'secondary'}>{p.status}</Badge></TableCell>
                <TableCell><RecordActions doctype="statusprofiles" onView={() => setViewItem(p)} onEdit={() => handleEdit(p)} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editMode ? 'Edit' : 'Create'} Status Profile</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Profile ID *</Label><Input value={formData.profile_id} onChange={(e) => setFormData({...formData, profile_id: e.target.value.toUpperCase()})} disabled={editMode} /></div>
              <div className="space-y-2"><Label>Name *</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} /></div>
              <div className="space-y-2"><Label>Object Type</Label><Select value={formData.object_type} onValueChange={(v) => setFormData({...formData, object_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="PROJECT">Project</SelectItem><SelectItem value="WBS">WBS Element</SelectItem><SelectItem value="NETWORK">Network</SelectItem><SelectItem value="ACTIVITY">Activity</SelectItem></SelectContent></Select></div>
              <div className="space-y-2"><Label>Status</Label><Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem></SelectContent></Select></div>
              <div className="col-span-2 space-y-2"><Label>Description</Label><Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} /></div>
            </div>
            <div className="border-t pt-4">
              <h3 className="font-medium mb-3">User Statuses</h3>
              <div className="grid grid-cols-4 gap-2 mb-4 p-3 bg-gray-50 rounded">
                <div className="space-y-1"><Label className="text-xs">Number</Label><Input type="number" min="1" max="99" value={newStatus.status_number} onChange={(e) => setNewStatus({...newStatus, status_number: parseInt(e.target.value)})} /></div>
                <div className="space-y-1"><Label className="text-xs">Code *</Label><Input value={newStatus.status_code} onChange={(e) => setNewStatus({...newStatus, status_code: e.target.value.toUpperCase()})} placeholder="APPR" /></div>
                <div className="space-y-1"><Label className="text-xs">Name *</Label><Input value={newStatus.status_name} onChange={(e) => setNewStatus({...newStatus, status_name: e.target.value})} placeholder="Approved" /></div>
                <div className="flex items-end">{writable && (<Button size="sm" onClick={addStatus}><Plus className="h-4 w-4 mr-1" />Add</Button>)}</div>
              </div>
              {formData.user_statuses.length > 0 && (
                <Table>
                  <TableHeader><TableRow><TableHead className="w-[60px]">#</TableHead><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead className="w-[60px]"></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {formData.user_statuses.map((s, i) => (
                      <TableRow key={i}><TableCell>{s.status_number}</TableCell><TableCell className="font-medium">{s.status_code}</TableCell><TableCell>{s.status_name}</TableCell><TableCell><Button variant="ghost" size="icon" onClick={() => removeStatus(i)}><Trash2 className="h-4 w-4 text-red-500" /></Button></TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button><Button onClick={handleSubmit}>{editMode ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default StatusProfiles;
